/**
 * The guest wishlist: what a logged-out visitor has saved, held in the browser
 * until they sign in and it merges into their account.
 *
 * Only ids are kept, never a snapshot of name or price — a saved item may sit
 * here for weeks, and a stale price rendered as fact is worse than a row that
 * has to be fetched. The wishlist page hydrates these ids through the existing
 * `products` query; hearts elsewhere need nothing but the variant id.
 *
 * A product id rides along because the schema has no root variant-list field:
 * variants can only be filtered underneath a product, so a bare variant id
 * would cost one round trip each.
 */

export interface GuestWishlistEntry {
  variantId: string
  productId: string
}

/** Serialised as {v,p} pairs — this sits in localStorage and is read on every visit. */
interface StoredEntry {
  v: string
  p: string
}

// A guard against a runaway or hand-edited store, not a product limit; nobody
// hearts more than this before signing in.
const MAX_ENTRIES = 200

function toId(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

/**
 * Read whatever is in storage as defensively as possible. Anything malformed —
 * another origin's key, a half-written value, a hand-edited array — degrades to
 * an empty list rather than throwing on a page the visitor is just browsing.
 */
export function parseGuestWishlist(raw: unknown): GuestWishlistEntry[] {
  if (typeof raw !== 'string' || raw === '')
    return []

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  }
  catch {
    return []
  }

  if (!Array.isArray(parsed))
    return []

  const entries: GuestWishlistEntry[] = []
  const seen = new Set<string>()

  for (const candidate of parsed) {
    if (typeof candidate !== 'object' || candidate === null)
      continue

    const { v, p } = candidate as Partial<StoredEntry>
    const variantId = toId(v)
    const productId = toId(p)

    if (!variantId || !productId || seen.has(variantId))
      continue

    seen.add(variantId)
    entries.push({ variantId, productId })

    if (entries.length >= MAX_ENTRIES)
      break
  }

  return entries
}

export function serializeGuestWishlist(entries: GuestWishlistEntry[]): string {
  return JSON.stringify(entries.map<StoredEntry>(entry => ({ v: entry.variantId, p: entry.productId })))
}

/**
 * The variant ids a merge has to send: everything held locally that the account
 * does not already have. Deduplicating here rather than relying on the server
 * being idempotent is what makes the union provably duplicate-free.
 */
export function guestOnlyVariantIds(
  entries: GuestWishlistEntry[],
  accountVariantIds: Iterable<string | number>,
): string[] {
  const onAccount = new Set<string>()
  for (const id of accountVariantIds)
    onAccount.add(String(id))

  const missing: string[] = []
  const seen = new Set<string>()

  for (const entry of entries) {
    if (onAccount.has(entry.variantId) || seen.has(entry.variantId))
      continue

    seen.add(entry.variantId)
    missing.push(entry.variantId)
  }

  return missing
}
